import AreaSelection from "Components/AreaSelection/AreaSelection";
import ConfirmToLeave from "Components/Common/ConfirmToLeave/ConfirmToLeave";
import ContentView from "Components/ContentView/ContentView";
import DeleteModal from "Components/DeleteModal/DeleteModal";
import Loader from "Components/Loading/Loader/Loader";
import { ITaskTemplateDetailViewUtil } from "Interfaces/Utils/ITaskTemplateDetailViewUtil";
import { Area } from "Models/Areas/Area";
import { DataState } from "Models/Common/DataState";
import { LinkType } from "Models/Tasks/LinkType";
import { MachineStatusType } from "Models/Tasks/MachineStatusType";
import { TaskRecurrency } from "Models/Tasks/TaskRecurrencies/TaskRecurrency";
import { TaskTemplate } from "Models/Tasks/TaskTemplate";
import { TaskTemplateStep } from "Models/Tasks/TaskTemplateStep";
import { RoleType } from "Models/Users/RoleType";
import "rc-dialog/assets/index.css";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IApplicationState } from "Store/Store";
import { ITaskTemplateAction } from "Store/TaskTemplates/TaskTemplateAction";
import { ITaskTemplateState } from "Store/TaskTemplates/TaskTemplateState";
import i18n from "Translations/i18n";
import TaskTemplateDetailHeader from "./TaskTemplateDetailHeader/TaskTemplateDetailHeader";
import styles from "./TaskTemplateDetailView.scss";
import { TaskTemplateInfo } from "./TaskTemplateInfo/TaskTemplateInfo";
import TaskTemplateRecurrencyView from "./TaskTemplateRecurrencyView/TaskTemplateRecurrencyView";
import TaskTemplateSettings from "./TaskTemplateSettings/TaskTemplateSettings";
import { TaskTemplateSteps } from "./TaskTemplateSteps/TaskTemplateSteps";

const taskTemplateAction = ServiceProvider.get<ITaskTemplateAction>(Types.ITaskTemplateAction);
const taskTemplateDetailViewUtil = ServiceProvider.get<ITaskTemplateDetailViewUtil>(Types.ITaskTemplateDetailViewUtil);

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getTaskTemplateByIdAsync: (taskTemplateId: number) =>
        dispatch(taskTemplateAction.getTaskTemplateByIdAsync(taskTemplateId)),
    saveTaskTemplateAsync: (taskTemplate: TaskTemplate, pictureFile?: File, descriptionFile?: File, videoFile?: File, videoThumbnailFile?: File, addAnother?: boolean) =>
        dispatch(taskTemplateAction.saveTaskTemplateAsync(taskTemplate, pictureFile, descriptionFile, videoFile, videoThumbnailFile, addAnother)),
    deleteTaskByIdAsync: (taskTemplateId: number) =>
        dispatch(taskTemplateAction.deleteTaskTemplateByIdAsync(taskTemplateId)),
    clearValidationErrorsByIdAsync: (taskTemplateId?: number) =>
        dispatch(taskTemplateAction.clearValidationErrorsByIdAsync(taskTemplateId))
});

const mapStateToProps = (state: IApplicationState) => ({
    taskTemplateState: state.taskTemplateState
});

interface IRouteParams {
    taskTemplateId: string; // must be type string since route params
}

interface IProps extends RouteComponentProps<IRouteParams> {
    taskTemplateState: ITaskTemplateState;
    getTaskTemplateByIdAsync: (taskTemplateId: number) => Action;
    saveTaskTemplateAsync: (taskTemplate: TaskTemplate, pictureFile?: File, descriptionFile?: File, videoFile?: File, videoThumbnailFile?: File, addAnother?: boolean) => Action;
    deleteTaskByIdAsync: (taskTemplateId: number) => Action;
    duplicateTaskTemplateByIdAsync: (taskTemplateId: number) => Action;
    clearValidationErrorsByIdAsync: (taskTemplateId?: number) => Action;
}

interface IState {
    taskTemplate?: TaskTemplate;
    isLoading: boolean;
    isOpenDeleteModal: boolean;
    pictureFile?: File;
    descriptionFile?: File;
    videoFile?: File;
    videoThumbnailFile?: File;
    validationErrors: string[];
    confirmToLeave: boolean;
}

export class TaskTemplateDetailView extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.loadTaskTemplate();
        this.taskTemplateUpdated = this.taskTemplateUpdated.bind(this);
        this.onInfoUpdated = this.onInfoUpdated.bind(this);
        this.onStepsUpdated = this.onStepsUpdated.bind(this);
        this.onAreaSelectionChanged = this.onAreaSelectionChanged.bind(this);
        this.onRecurrencyChanged = this.onRecurrencyChanged.bind(this);
        this.onMachineStatusChanged = this.onMachineStatusChanged.bind(this);
        this.loadTaskTemplate = this.loadTaskTemplate.bind(this);
        this.onDeeplinkChanged = this.onDeeplinkChanged.bind(this);
        this.onRoleChanged = this.onRoleChanged.bind(this);
        this.onPlannedTimeChanged = this.onPlannedTimeChanged.bind(this);
        this.onTaskDeletedClicked = this.onTaskDeletedClicked.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
        this.delete = this.delete.bind(this);
        this.performDelete = this.performDelete.bind(this);
        this.duplicate = this.duplicate.bind(this);
        this.performDuplicate = this.performDuplicate.bind(this);
        this.save = this.save.bind(this);
        this.performSave = this.performSave.bind(this);
        this.saveAndAddAnother = this.saveAndAddAnother.bind(this);
        this.performSaveAndAddAnother = this.performSaveAndAddAnother.bind(this);
    }

    private loadTaskTemplate() {
        const templateIdParam = this.props.match.params.taskTemplateId;
        if (templateIdParam && templateIdParam !== "new") {
            const taskTemplateId = parseInt(this.props.match.params.taskTemplateId, 0);
            this.props.getTaskTemplateByIdAsync(taskTemplateId);
            this.state = {
                isLoading: true,
                isOpenDeleteModal: false,
                validationErrors: [],
                confirmToLeave: false
            };
            this.props.clearValidationErrorsByIdAsync(taskTemplateId);
        }
        else {
            this.state = {
                isLoading: false,
                isOpenDeleteModal: false,
                taskTemplate: new TaskTemplate(),
                validationErrors: [],
                confirmToLeave: false
            };
            this.props.clearValidationErrorsByIdAsync();
        }
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        let taskTemplateId: number = -1;
        const partialState: Partial<IState> = {};

        if (nextProps.match.params.taskTemplateId && nextProps.match.params.taskTemplateId !== "new") {
            taskTemplateId = parseInt(nextProps.match.params.taskTemplateId, 0);

            if (
                nextProps.taskTemplateState.detailTaskTemplates &&
                nextProps.taskTemplateState.detailTaskTemplates[taskTemplateId] &&
                (
                    !prevState.taskTemplate ||
                    !prevState.taskTemplate.id ||
                    nextProps.taskTemplateState.detailTaskTemplates[taskTemplateId].lastUpdatedAt > prevState.taskTemplate.lastUpdatedAt
                )
            ) {
                partialState.taskTemplate = nextProps.taskTemplateState.detailTaskTemplates[taskTemplateId];
                partialState.confirmToLeave = false;
            }
            if (nextProps.taskTemplateState.detailTaskTemplateStates) {
                const templateState = nextProps.taskTemplateState.detailTaskTemplateStates[taskTemplateId];
                if (templateState === DataState.Deleted) {
                    nextProps.history.push("/tasktemplates");
                }
                else if ((templateState === DataState.Loading) !== prevState.isLoading) {
                    partialState.isLoading = templateState === DataState.Loading;
                }
            }
        }

        if (nextProps.taskTemplateState.detailTaskTemplateValidationErrors
            && nextProps.taskTemplateState.detailTaskTemplateValidationErrors[taskTemplateId]
            && nextProps.taskTemplateState.detailTaskTemplateValidationErrors[taskTemplateId] !== prevState.validationErrors) {
            partialState.validationErrors = nextProps.taskTemplateState.detailTaskTemplateValidationErrors[taskTemplateId];
        }

        return partialState;
    }

    public taskTemplateUpdated() {
        this.setState({ confirmToLeave: true });
    }

    public onInfoUpdated(name: string, description: string, isMediaChanged: boolean, pictureFile?: File, videoFile?: File, videoThumbnailFile?: File) {
        let template = this.state.taskTemplate;
        if (isMediaChanged) {
            template = {
                ...template!,
                pictureUrl: pictureFile ? URL.createObjectURL(pictureFile) : undefined,
                videoUrl: videoFile ? videoFile !== this.state.videoFile ? URL.createObjectURL(videoFile) : template!.videoUrl : videoThumbnailFile ? template!.videoUrl : undefined,
                videoThumbnailUrl: videoThumbnailFile ? URL.createObjectURL(videoThumbnailFile) : undefined
            };
        }

        this.setState({
            taskTemplate: {
                ...template!,
                name,
                description
            },
            pictureFile,
            videoFile,
            videoThumbnailFile
        });
        this.taskTemplateUpdated();
    }

    public onStepsUpdated(steps: TaskTemplateStep[], descriptionFile?: File) {
        this.setState({
            taskTemplate: {
                ...this.state.taskTemplate!,
                steps,
                descriptionFileUrl: descriptionFile ? descriptionFile.name : undefined
            },
            descriptionFile
        });
        this.taskTemplateUpdated();
    }

    public onAreaSelectionChanged(selectedArea?: Area) {
        this.setState({
            taskTemplate: {
                ...this.state.taskTemplate!,
                area: selectedArea
            }
        });
        this.taskTemplateUpdated();
    }

    public onRecurrencyChanged(taskRecurrency: TaskRecurrency) {
        const newTaskTemplate = { ...this.state.taskTemplate! };
        newTaskTemplate.taskRecurrencies[0] = taskRecurrency;
        this.setState({ taskTemplate: newTaskTemplate });
        this.taskTemplateUpdated();
    }

    public onMachineStatusChanged(newMachineStatus: MachineStatusType) {
        this.setState({
            taskTemplate: {
                ...this.state.taskTemplate!,
                machineStatus: newMachineStatus
            }
        });
        this.taskTemplateUpdated();
    }

    public onRoleChanged(newRole: RoleType) {
        this.setState({
            taskTemplate: {
                ...this.state.taskTemplate!,
                role: newRole
            }
        });
        this.taskTemplateUpdated();
    }

    public onPlannedTimeChanged(newValue: number) {
        this.setState({
            taskTemplate: {
                ...this.state.taskTemplate!,
                plannedTime: newValue
            }
        });
        this.taskTemplateUpdated();
    }

    public onTaskDeletedClicked() {
        this.setState({
            isOpenDeleteModal: true
        });
    }

    public closeDeleteDialog() {
        this.setState({
            isOpenDeleteModal: false
        });
    }

    public delete() {
        if (this.state.taskTemplate && this.state.taskTemplate.id) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performDelete);
        }
    }

    public performDelete() {
        if (this.state.taskTemplate && this.state.taskTemplate.id) {
            this.props.deleteTaskByIdAsync(this.state.taskTemplate.id);
        }
    }

    public onDeeplinkChanged(newLinkType: LinkType, newLinkId?: number) {
        this.setState({
            taskTemplate: {
                ...this.state.taskTemplate!,
                deeplinkId: newLinkId,
                deeplinkTo: newLinkType
            }
        });
        this.taskTemplateUpdated();
    }

    public duplicate() {
        if (this.state.taskTemplate) {
            window.setTimeout(this.performDuplicate);

            this.setState({
                validationErrors: []
            });

            this.props.clearValidationErrorsByIdAsync();
        }
    }

    private performDuplicate() {
        if (this.state.taskTemplate) {
            this.setState({ confirmToLeave: false });
            const duplicatedTaskTemplate = taskTemplateDetailViewUtil.duplicateTaskTemplate(this.state.taskTemplate);
            this.props.history.push(`/tasktemplates/new`);
            this.setState({
                taskTemplate: duplicatedTaskTemplate
            });
            this.taskTemplateUpdated();
        }
    }

    public save() {
        if (this.state.taskTemplate) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performSave);
        }
    }

    private performSave() {
        if (this.state.taskTemplate) {
            this.props.saveTaskTemplateAsync(this.state.taskTemplate, this.state.pictureFile, this.state.descriptionFile, this.state.videoFile, this.state.videoThumbnailFile);
        }
    }

    public saveAndAddAnother() {
        if (this.state.taskTemplate) {
            this.setState({ confirmToLeave: false });
            window.setTimeout(this.performSaveAndAddAnother);
        }
    }

    private performSaveAndAddAnother() {
        if (this.state.taskTemplate) {
            this.props.saveTaskTemplateAsync(this.state.taskTemplate, this.state.pictureFile, this.state.descriptionFile, this.state.videoFile, this.state.videoThumbnailFile, true);
            this.setState({
                taskTemplate: { ...this.state.taskTemplate, id: undefined }
            });
        }
    }

    public render() {
        return (
            <div id="divTaskTemplateDetailView">
                {this.state.taskTemplate ? (
                    <>
                        <ConfirmToLeave confirmToLeave={this.state.confirmToLeave} />
                        <TaskTemplateDetailHeader onTaskDeletedClicked={this.onTaskDeletedClicked} duplicate={this.duplicate} save={this.save} saveAndAddAnother={this.saveAndAddAnother} taskTemplate={this.state.taskTemplate} />
                        <ContentView className={`${styles.validationErrors} ${this.state.validationErrors && this.state.validationErrors.length ? styles.visible : styles.hidden}`}>
                            <div className={styles.error}>
                                <h2>{i18n.t("tasks.validationErrors.foundErrors")}</h2>
                                <ul>
                                    {
                                        this.state.validationErrors.map((validationError, index) => {
                                            return (<li key={validationError + "-" + index}>{i18n.t(`tasks.validationErrors.${validationError}`)}</li>);
                                        })
                                    }
                                </ul>
                            </div>
                        </ContentView>
                        <TaskTemplateInfo taskTemplate={this.state.taskTemplate} onChange={this.onInfoUpdated} />
                        <TaskTemplateSteps taskTemplate={this.state.taskTemplate} onChange={this.onStepsUpdated} />
                        <AreaSelection className={styles.areaSearch} backgroundIsGray={true} selectedArea={this.state.taskTemplate.area} onSelectionChanged={this.onAreaSelectionChanged} showAreaNavigator={true} title={i18n.t("tasks.area.title")} />
                        <TaskTemplateRecurrencyView area={this.state.taskTemplate.area} taskRecurrency={this.state.taskTemplate.taskRecurrencies && this.state.taskTemplate.taskRecurrencies.length > 0 ? this.state.taskTemplate.taskRecurrencies[0] : new TaskRecurrency()} onRecurrencyChanged={this.onRecurrencyChanged} />
                        <TaskTemplateSettings currentPlannedTime={this.state.taskTemplate.plannedTime} selectedRole={this.state.taskTemplate.role} selectedLinkType={this.state.taskTemplate.deeplinkTo} selectedLinkId={(this.state.taskTemplate.deeplinkId) ? this.state.taskTemplate.deeplinkId : null} selectedMachineStatusType={this.state.taskTemplate.machineStatus} onPlannedTimeChanged={this.onPlannedTimeChanged} onRoleChanged={this.onRoleChanged} onDeeplinkChanged={this.onDeeplinkChanged} onMachineStatusChanged={this.onMachineStatusChanged} />
                        <DeleteModal visible={this.state.isOpenDeleteModal} onDelete={this.delete} onCancel={this.closeDeleteDialog} message={i18n.t("tasks.confirmDeleteText")} />
                    </>
                ) : this.state.isLoading ? (
                    <Loader />
                ) : (
                            <span id="spnTaskTemplateNotFound">{i18n.t("tasks.taskTemplateNotFound")}</span>
                        )}
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(TaskTemplateDetailView);


// WEBPACK FOOTER //
// ./src/Components/Pages/TaskTemplateDetailView/TaskTemplateDetailView.tsx